import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PrototypeLayout from '../PrototypeLayout';
import { colors, font, type as ty, layout } from '../theme/tokens';
import { heat } from '../theme/tokens';
import { panel, Badge, PageHeader, PanelTitle, DemoFooter, Tone } from '../components/ui';
import {
  ANOMALY_DOOR_IDS as DOOR_IDS, WEEKS, CycleEntry,
  getHeatmap, getDoorCycles, worstAnomalyDoor,
} from '../data/anomaly';
import { doorLabel } from '../data/doors';
import { useFleet } from '../FleetContext';
import { useI18n } from '../i18n';

// ── Constants ────────────────────────────────────────────────────────────────

const METHODS = ['Isolation\nForest', 'LOF', 'One-Class\nSVM', 'Autoencoder'];

// Method colours: distinct on white, and none of them is the brand yellow
// (which is reserved for selection and chrome).
const METHOD_COLORS = ['#4456F2', '#2E7D53', '#7A4FA3', '#B58900'];

// ── Types ────────────────────────────────────────────────────────────────────


// ── Data ─────────────────────────────────────────────────────────────────────


// ── Helpers ──────────────────────────────────────────────────────────────────

/** Map a HIGH-cycle percentage onto the shared severity ramp. */
function heatCell(pct: number) {
  const idx = pct < 10 ? 1 : pct < 20 ? 2 : pct < 35 ? 3 : pct < 55 ? 4 : 5;
  return heat[idx]!;
}

function scoreColor(s: number) {
  if (s < 25) return colors.ok;
  if (s < 50) return colors.warn;
  return colors.alert;
}

function scoreTone(s: number): Tone {
  if (s < 25) return 'ok';
  if (s < 50) return 'warn';
  return 'alert';
}

function typeColor(t: string) {
  if (t === 'normal') return colors.ok;
  if (t === 'borderline') return colors.warn;
  return colors.alert;
}

function cycleClassColor(c: string) {
  const m: Record<string, string> = {
    FULL_CYCLE: colors.ok,
    OBSTRUCTED_CLOSE: colors.warn,
    MULTI_ATTEMPT: colors.alert,
    OPEN_ONLY: colors.info,
  };
  return m[c] ?? colors.warn;
}

function cycleClassKey(c: string) {
  const m: Record<string, string> = {
    FULL_CYCLE: 'p4.normalClose',
    OBSTRUCTED_CLOSE: 'p4.obstructed',
    MULTI_ATTEMPT: 'p4.multiAttempt',
    OPEN_ONLY: 'p4.openOnly',
  };
  return m[c] ?? c;
}

function prepCanvas(canvas: HTMLCanvasElement, cssW: number, cssH: number) {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = cssW * dpr;
  canvas.height = cssH * dpr;
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

// ── Radar ────────────────────────────────────────────────────────────────────

const RadarChart: React.FC<{ cycles: CycleEntry[]; selectedId: string | null }> = ({ cycles, selectedId }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const W = 360, H = 330;
    const ctx = prepCanvas(canvas, W, H);
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);

    const cx = W / 2, cy = H / 2 + 8;
    const R = Math.min(W, H) * 0.31;
    const N = METHODS.length;
    const angles = METHODS.map((_, i) => (i / N) * Math.PI * 2 - Math.PI / 2);
    const c = ctx;

    const pt = (score: number, i: number): [number, number] => {
      const r = (score / 100) * R;
      return [cx + r * Math.cos(angles[i]), cy + r * Math.sin(angles[i])];
    };

    // Rings
    [20, 40, 60, 80, 100].forEach(level => {
      c.beginPath();
      angles.forEach((a, i) => {
        const r = (level / 100) * R;
        const x = cx + r * Math.cos(a), y = cy + r * Math.sin(a);
        if (i === 0) c.moveTo(x, y); else c.lineTo(x, y);
      });
      c.closePath();
      c.strokeStyle = level === 60 ? colors.accentDeep : colors.border;
      c.lineWidth = level === 60 ? 1.5 : 1;
      c.setLineDash(level === 60 ? [4, 3] : []);
      c.stroke();
      c.setLineDash([]);

      if (level < 100) {
        c.fillStyle = colors.inkGhost;
        c.font = `9px ${font.mono}`;
        c.textAlign = 'right';
        const rx = cx + (level / 100) * R * Math.cos(angles[0]);
        const ry = cy + (level / 100) * R * Math.sin(angles[0]);
        c.fillText(String(level), rx - 4, ry + 3);
      }
    });

    // Threshold caption
    c.fillStyle = colors.accentDeep;
    c.font = `9px ${font.mono}`;
    c.textAlign = 'left';
    const tx = cx + 0.6 * R * Math.cos(angles[1]);
    const tyy = cy + 0.6 * R * Math.sin(angles[1]);
    c.fillText('threshold', tx + 5, tyy);

    // Spokes
    angles.forEach(a => {
      c.beginPath();
      c.moveTo(cx, cy);
      c.lineTo(cx + R * Math.cos(a), cy + R * Math.sin(a));
      c.strokeStyle = colors.border;
      c.lineWidth = 1;
      c.stroke();
    });

    // Polygons — unselected faint, selected solid
    cycles.forEach(cycle => {
      const isSelected = cycle.id === selectedId;
      const base = typeColor(cycle.type);

      c.beginPath();
      cycle.scores.slice(0, 4).forEach((score, i) => {
        const [x, y] = pt(score, i);
        if (i === 0) c.moveTo(x, y); else c.lineTo(x, y);
      });
      c.closePath();

      if (isSelected) {
        c.fillStyle = base + '2E';
        c.fill();
        c.strokeStyle = base;
        c.lineWidth = 2.5;
      } else {
        c.strokeStyle = colors.borderStrong;
        c.lineWidth = 1;
      }
      c.stroke();

      if (isSelected) {
        cycle.scores.slice(0, 4).forEach((score, i) => {
          const [x, y] = pt(score, i);
          c.beginPath();
          c.arc(x, y, 4.5, 0, Math.PI * 2);
          c.fillStyle = base;
          c.fill();
          c.strokeStyle = colors.bg;
          c.lineWidth = 1.5;
          c.stroke();
        });
      }
    });

    // Axis labels
    c.font = `10px ${font.mono}`;
    c.textAlign = 'center';
    angles.forEach((a, i) => {
      const labelR = R + 26;
      const lx = cx + labelR * Math.cos(a);
      const ly = cy + labelR * Math.sin(a);
      c.fillStyle = METHOD_COLORS[i];
      const lines = METHODS[i].split('\n');
      lines.forEach((line, li) => {
        c.fillText(line, lx, ly + li * 12 - (lines.length - 1) * 6);
      });
    });
  }, [cycles, selectedId]);

  return (
    <canvas
      ref={canvasRef}
      style={{ display: 'block', width: '100%', height: 'auto', maxWidth: 360, margin: '0 auto', aspectRatio: '360 / 330' }}
    />
  );
};

// ── Fleet heatmap ────────────────────────────────────────────────────────────

const AnomalyHeatmap: React.FC<{
  heatmap: number[][];
  selectedDoor: string;
  onSelectDoor: (id: string) => void;
}> = ({ heatmap, selectedDoor, onSelectDoor }) => {
  const { t } = useI18n();
  return (
  <Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: '58px repeat(8,1fr)', gap: '4px', mb: '4px' }}>
      <Box />
      {WEEKS.map(w => (
        <Typography key={w} sx={{
          fontFamily: font.mono, fontSize: '10px', fontWeight: 500,
          color: colors.inkFaint, textAlign: 'center', letterSpacing: '0.04em',
        }}>
          {w}
        </Typography>
      ))}
    </Box>

    {DOOR_IDS.map((door, di) => {
      const isSelected = selectedDoor === door;
      const rowMax = Math.max(...heatmap[di]);
      return (
        <Box
          key={door}
          onClick={() => onSelectDoor(door)}
          role="button"
          tabIndex={0}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectDoor(door); }
          }}
          sx={{
            display: 'grid',
            gridTemplateColumns: '58px repeat(8,1fr)',
            gap: '4px',
            mb: '4px',
            cursor: 'pointer',
            outline: 'none',
            '&:hover .door-lbl, &:focus-visible .door-lbl': { color: colors.ink },
          }}
        >
          <Typography
            className="door-lbl"
            sx={{
              fontFamily: font.mono, fontSize: '11px',
              fontWeight: isSelected ? 700 : 500,
              color: isSelected ? colors.ink : colors.inkMuted,
              display: 'flex', alignItems: 'center', gap: 0.5,
              transition: 'color .15s', pr: 1,
            }}
          >
            {doorLabel(door)}
            {rowMax >= 35 && (
              <Box component="span" sx={{
                width: 5, height: 5, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
                backgroundColor: rowMax >= 55 ? colors.alert : colors.warn,
              }} />
            )}
          </Typography>

          {heatmap[di].map((pct, wi) => {
            const h = heatCell(pct);
            return (
              <Box key={wi} sx={{
                height: 32,
                borderRadius: '2px',
                backgroundColor: h.fill,
                border: isSelected ? `1px solid ${colors.blue}` : `1px solid ${h.stroke}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'border-color .15s',
              }}>
                <Typography sx={{ fontFamily: font.mono, fontSize: '11px', fontWeight: 500, color: h.text }}>
                  {pct}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    })}

    <Box sx={{
      display: 'flex', gap: 2, mt: 2, pt: 2, flexWrap: 'wrap',
      borderTop: `1px solid ${colors.border}`,
    }}>
      {[
        { key: 'p4.legend.normal', i: 1 },
        { key: 'p4.legend.low', i: 2 },
        { key: 'p4.legend.elevated', i: 3 },
        { key: 'p4.legend.high', i: 4 },
        { key: 'p4.legend.critical', i: 5 },
      ].map(l => {
        const h = heat[l.i]!;
        return (
          <Box key={l.key} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box sx={{
              width: 13, height: 13, borderRadius: '2px',
              backgroundColor: h.fill, border: `1px solid ${h.stroke}`, flexShrink: 0,
            }} />
            <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkMuted }}>
              {t(l.key)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Box>
  );
};

// ── Score bar ────────────────────────────────────────────────────────────────

const ScoreBar: React.FC<{ label: string; score: number; color: string }> = ({ label, score, color }) => (
  <Box sx={{ mb: 1.25 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography sx={{ fontFamily: font.mono, fontSize: '11px', color: colors.inkMuted }}>{label}</Typography>
      <Typography sx={{ fontFamily: font.mono, fontSize: '11px', fontWeight: 700, color }}>{score}</Typography>
    </Box>
    <Box sx={{
      height: 6, borderRadius: '2px', backgroundColor: colors.bgAlt,
      border: `1px solid ${colors.border}`, overflow: 'hidden',
    }}>
      <Box sx={{ width: `${score}%`, height: '100%', backgroundColor: color, transition: 'width .35s ease' }} />
    </Box>
  </Box>
);

// ── Page ─────────────────────────────────────────────────────────────────────

const Prototype4: React.FC = () => {
  const { unitId, caption } = useFleet();
  const { t } = useI18n();
  const [selectedDoor, setSelectedDoor] = useState<string>(() => worstAnomalyDoor(unitId));
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);

  const heatmap = useMemo(() => getHeatmap(unitId), [unitId]);
  const doorCycles = useMemo(() => getDoorCycles(unitId, selectedDoor), [unitId, selectedDoor]);

  // When the unit changes, jump to that unit's worst door rather than keeping a
  // selection that may be unremarkable on the new vehicle.
  useEffect(() => {
    setSelectedDoor(worstAnomalyDoor(unitId));
  }, [unitId]);

  // Auto-select the most anomalous cycle whenever the door changes.
  useEffect(() => {
    const worst = [...doorCycles].sort((a, b) => b.scores[4] - a.scores[4])[0];
    setSelectedCycleId(worst?.id ?? null);
  }, [doorCycles]);

  const selectedCycle = doorCycles.find(c => c.id === selectedCycleId) ?? doorCycles[0];
  const di = DOOR_IDS.indexOf(selectedDoor);
  const rowData = heatmap[di] ?? heatmap[0];
  const peakPct = Math.max(...rowData);
  const avgPct = Math.round(rowData.reduce((a, b) => a + b, 0) / rowData.length);
  const worstWeek = WEEKS[rowData.indexOf(peakPct)];

  return (
    <PrototypeLayout protoId="04" titleKey="proto.04.title">
      <PageHeader
        eyebrow={t('p4.approach')}
        title={t('p4.title')}
        right={
          <>
            <Badge label={t('common.syntheticData')} t="info" />
            <Typography sx={{ ...ty.meta, color: colors.inkFaint }}>
              {caption} · {t('p4.weeks')}
            </Typography>
          </>
        }
      />

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: 3,
        mb: 3,
      }}>
        {/* Door detail */}
        <Box sx={panel}>
          <PanelTitle right={<Badge label={doorLabel(selectedDoor)} t="accent" />}>
            {t('p4.step2')}
          </PanelTitle>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1.5, mb: 3 }}>
            {[
              { label: t('p4.peakWeek'), value: `${peakPct}%`, sub: worstWeek, color: scoreColor(peakPct) },
              { label: t('p4.avgAnomaly'), value: `${avgPct}%`, sub: t('p4.eightWeekAvg'), color: scoreColor(avgPct) },
              { label: t('p4.cyclesShown'), value: String(doorCycles.length), sub: t('p4.representative'), color: colors.ink },
            ].map(s => (
              <Box key={s.label} sx={{
                backgroundColor: colors.bgAlt,
                border: `1px solid ${colors.border}`,
                borderRadius: layout.radius,
                p: 1.5,
              }}>
                <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint, mb: 0.75 }}>
                  {s.label}
                </Typography>
                <Typography sx={{
                  fontFamily: font.sans, fontSize: '22px', fontWeight: 700,
                  letterSpacing: '-0.02em', color: s.color, lineHeight: 1,
                }}>
                  {s.value}
                </Typography>
                <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkFaint, mt: 0.5 }}>
                  {s.sub}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography sx={{ ...ty.eyebrow, fontSize: '10px', color: colors.inkFaint, mb: 1.5 }}>
            {t('p4.selectCycle')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {doorCycles.map(cycle => {
              const isSelected = cycle.id === selectedCycleId;
              return (
                <Box
                  key={cycle.id}
                  onClick={() => setSelectedCycleId(cycle.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedCycleId(cycle.id); }
                  }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                    borderRadius: layout.radius,
                    cursor: 'pointer',
                    outline: 'none',
                    backgroundColor: isSelected ? colors.accentWash : colors.bg,
                    border: `1px solid ${isSelected ? colors.accent : colors.border}`,
                    borderLeft: `3px solid ${typeColor(cycle.type)}`,
                    transition: 'background-color .15s, border-color .15s',
                    '&:hover, &:focus-visible': { backgroundColor: colors.bgAlt },
                  }}
                >
                  <Typography sx={{
                    fontFamily: font.mono, fontSize: '12px', fontWeight: 700,
                    color: colors.ink, width: 32,
                  }}>
                    {cycle.week}
                  </Typography>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Box sx={{
                      width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                      backgroundColor: cycleClassColor(cycle.cycleClass),
                    }} />
                    <Typography sx={{ fontFamily: font.sans, fontSize: '13px', color: colors.inkMuted }}>
                      {t(cycleClassKey(cycle.cycleClass))}
                    </Typography>
                  </Box>
                  <Typography sx={{
                    fontFamily: font.mono, fontSize: '14px', fontWeight: 700,
                    color: scoreColor(cycle.scores[4]),
                  }}>
                    {cycle.scores[4]}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Radar */}
        <Box sx={panel}>
          <PanelTitle>{t('p4.step1')}</PanelTitle>
          <Typography sx={{
            fontFamily: font.sans, fontSize: '13px', color: colors.inkMuted,
            mb: 2, lineHeight: 1.6,
          }}>
            {t('p4.radarIntro')}
          </Typography>

          <RadarChart cycles={doorCycles} selectedId={selectedCycleId} />

          {selectedCycle && (
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.border}` }}>
              <Box sx={{
                display: 'flex', alignItems: 'flex-start',
                justifyContent: 'space-between', gap: 2, mb: 2,
              }}>
                <Box>
                  <Typography sx={{
                    fontFamily: font.sans, fontSize: '15px', fontWeight: 700,
                    letterSpacing: '-0.01em', color: colors.ink,
                  }}>
                    {doorLabel(selectedDoor)} · {selectedCycle.week}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                    <Box sx={{
                      width: 6, height: 6, borderRadius: '50%',
                      backgroundColor: cycleClassColor(selectedCycle.cycleClass),
                    }} />
                    <Typography sx={{ fontFamily: font.sans, fontSize: '13px', color: colors.inkMuted }}>
                      {t(cycleClassKey(selectedCycle.cycleClass))}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint }}>
                    {t('p4.ensemble')}
                  </Typography>
                  <Typography sx={{
                    fontFamily: font.sans, fontSize: '36px', fontWeight: 700,
                    letterSpacing: '-0.03em', lineHeight: 1,
                    color: scoreColor(selectedCycle.scores[4]),
                  }}>
                    {selectedCycle.scores[4]}
                  </Typography>
                  <Box sx={{ mt: 0.75 }}>
                    <Badge
                      label={scoreTone(selectedCycle.scores[4]) === 'ok' ? t('common.nominal') : scoreTone(selectedCycle.scores[4]) === 'warn' ? t('p4.review') : t('p4.inspect')}
                      t={scoreTone(selectedCycle.scores[4])}
                    />
                  </Box>
                </Box>
              </Box>

              {METHODS.map((m, i) => (
                <ScoreBar key={m} label={m.replace('\n', ' ')} score={selectedCycle.scores[i]} color={METHOD_COLORS[i]} />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Fleet heatmap */}
      <Box sx={{ ...panel, mb: 3 }}>
        <PanelTitle>{t('p4.step3')}</PanelTitle>
        <Typography sx={{ fontFamily: font.sans, fontSize: '13px', color: colors.inkMuted, mb: 2.5 }}>
          {t('p4.heatmapIntro')}
        </Typography>
        <AnomalyHeatmap heatmap={heatmap} selectedDoor={selectedDoor} onSelectDoor={setSelectedDoor} />
      </Box>

      {/* Pipeline */}
      <Box sx={panel}>
        <PanelTitle>{t('p4.pipeline')}</PanelTitle>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)', lg: 'repeat(4,1fr)' },
          gap: 3,
        }}>
          {[
            { step: '01', titleKey: 'p4.pipe1.title', bodyKey: 'p4.pipe1.body' },
            { step: '02', titleKey: 'p4.pipe2.title', bodyKey: 'p4.pipe2.body' },
            { step: '03', titleKey: 'p4.pipe3.title', bodyKey: 'p4.pipe3.body' },
            { step: '04', titleKey: 'p4.pipe4.title', bodyKey: 'p4.pipe4.body' },
          ].map(item => (
            <Box key={item.step} sx={{ pl: 2, borderLeft: `3px solid ${colors.accent}` }}>
              <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint, mb: 1 }}>
                {t('p4.step')} {item.step}
              </Typography>
              <Typography sx={{
                fontFamily: font.sans, fontSize: '14px', fontWeight: 700,
                letterSpacing: '-0.01em', color: colors.ink, mb: 1, lineHeight: 1.3,
              }}>
                {t(item.titleKey)}
              </Typography>
              <Typography sx={{ fontFamily: font.sans, fontSize: '13px', color: colors.inkMuted, lineHeight: 1.6 }}>
                {t(item.bodyKey)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <DemoFooter note={`${t('common.syntheticNote')} · Isolation Forest · LOF · One-Class SVM · Autoencoder`} />
    </PrototypeLayout>
  );
};

export default Prototype4;
